import { ChevronDown, ChevronUp, Pencil, Plus, Save, ToggleLeft, ToggleRight, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { featureFlags } from "@/config/featureFlags";
import { teamService } from "@/lib/services";
import type { Agent, App, FlowSummary, TeamHubQuickActionSummary, TeamHub as TeamHubType } from "@/types";
import { ColorPicker, FormField, FormInput, FormTextarea, IconPicker } from "./FormWidgets";
import { GRADIENT_PRESETS, resolveIcon } from "./utils";

// ---------------------------------------------------------------------------
// Quick Action form data
// ---------------------------------------------------------------------------

export interface QuickActionFormData {
  label: string;
  description: string;
  icon: string;
  color: string;
  gradient: string;
  actionType: "agent" | "app" | "flow";
  agentUuid: string;
  appUuid: string;
  flowUuid: string;
  targetRoute: string;
  hintText: string;
  autoSubmit: boolean;
  isEnabled: boolean;
}

export const emptyQuickAction: QuickActionFormData = {
  label: "",
  description: "",
  icon: "zap",
  color: "text-blue-400",
  gradient: "from-blue-500/20 to-indigo-500/20",
  actionType: "agent",
  agentUuid: "",
  appUuid: "",
  flowUuid: "",
  targetRoute: "",
  hintText: "",
  autoSubmit: false,
  isEnabled: true,
};

// ---------------------------------------------------------------------------
// QuickActionForm
// ---------------------------------------------------------------------------

export function QuickActionForm({
  data,
  onChange,
  agents,
  apps,
  flows,
}: Readonly<{
  data: QuickActionFormData;
  onChange: (d: QuickActionFormData) => void;
  agents: Agent[];
  apps: App[];
  flows: FlowSummary[];
}>) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Label *">
          <FormInput
            value={data.label}
            onChange={(v) => onChange({ ...data, label: v })}
            placeholder="e.g., Research Prospect"
            maxLength={255}
          />
        </FormField>
        <FormField label="Action Type *">
          <select
            value={data.actionType}
            onChange={(e) =>
              onChange({
                ...data,
                actionType: e.target.value as "agent" | "app" | "flow",
                agentUuid: "",
                appUuid: "",
                flowUuid: "",
                targetRoute: "",
              })
            }
            className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
          >
            <option value="agent">Agent</option>
            {featureFlags.apps && <option value="app">App</option>}
            {featureFlags.workflows && <option value="flow">Flow</option>}
          </select>
        </FormField>
      </div>
      <FormField label="Description">
        <FormTextarea
          value={data.description}
          onChange={(v) => onChange({ ...data, description: v })}
          placeholder="Brief description of what this action does..."
        />
      </FormField>
      {data.actionType === "agent" && (
        <FormField label="Agent">
          <select
            value={data.agentUuid}
            onChange={(e) => onChange({ ...data, agentUuid: e.target.value })}
            className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
          >
            <option value="">None (generic chat)</option>
            {agents.map((a) => (
              <option key={a.uuid} value={a.uuid}>
                {a.name}
              </option>
            ))}
          </select>
        </FormField>
      )}
      {data.actionType === "app" && (
        <>
          <FormField label="App">
            <select
              value={data.appUuid}
              onChange={(e) => onChange({ ...data, appUuid: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
            >
              <option value="">Select an app...</option>
              {apps.map((a) => (
                <option key={a.uuid} value={a.uuid}>
                  {a.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Target Route">
            <FormInput
              value={data.targetRoute}
              onChange={(v) => onChange({ ...data, targetRoute: v })}
              placeholder="/apps/sowift/new"
            />
          </FormField>
        </>
      )}
      {data.actionType === "flow" && (
        <FormField label="Flow">
          <select
            value={data.flowUuid}
            onChange={(e) => onChange({ ...data, flowUuid: e.target.value })}
            className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
          >
            <option value="">Select a flow...</option>
            {flows.map((f) => (
              <option key={f.uuid} value={f.uuid}>
                {f.name}
              </option>
            ))}
          </select>
        </FormField>
      )}

      {/* Hint Text */}
      <FormField label="Hint Text">
        <FormInput
          value={data.hintText}
          onChange={(v) => onChange({ ...data, hintText: v })}
          placeholder="e.g., Enter company or client name..."
          maxLength={255}
        />
        <p className="text-[11px] text-content-quaternary mt-1">
          Placeholder text shown in the input box when a user expands this action
        </p>
      </FormField>

      {/* Appearance: Icon + Color + Gradient in a cleaner layout */}
      <div>
        <span className="block text-xs font-medium text-content-secondary mb-2">Appearance</span>
        <div className="bg-surface-secondary/50 border border-border-secondary rounded-lg p-3 space-y-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[11px] text-content-quaternary uppercase tracking-wider w-12 shrink-0">Icon</span>
              <IconPicker value={data.icon} onChange={(v) => onChange({ ...data, icon: v })} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[11px] text-content-quaternary uppercase tracking-wider w-12 shrink-0">Color</span>
            <ColorPicker value={data.color} onChange={(v) => onChange({ ...data, color: v })} />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[11px] text-content-quaternary uppercase tracking-wider w-12 shrink-0">Grad.</span>
            <ColorPicker
              value={data.gradient}
              onChange={(v) => onChange({ ...data, gradient: v })}
              presets={GRADIENT_PRESETS}
              isGradient
            />
          </div>
        </div>
      </div>

      {/* Auto-submit toggle */}
      <div className="flex items-center justify-between bg-surface-secondary/50 border border-border-secondary rounded-lg px-3 py-2.5">
        <div>
          <p className="text-xs font-medium text-content-secondary">Auto-submit</p>
          <p className="text-[11px] text-content-quaternary mt-0.5">
            Automatically start the chat or flow instead of just filling the input
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange({ ...data, autoSubmit: !data.autoSubmit })}
          className="shrink-0 ml-4"
        >
          {data.autoSubmit ? (
            <ToggleRight className="h-6 w-6 text-emerald-400" />
          ) : (
            <ToggleLeft className="h-6 w-6 text-content-quaternary" />
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuickActionsSection
// ---------------------------------------------------------------------------

export function QuickActionsSection({
  hub,
  agents,
  apps,
  flows,
  onRefresh,
}: Readonly<{
  hub: TeamHubType;
  agents: Agent[];
  apps: App[];
  flows: FlowSummary[];
  onRefresh: () => void;
}>) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [expanded, setExpanded] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [formData, setFormData] = useState<QuickActionFormData>(emptyQuickAction);
  const [saving, setSaving] = useState(false);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const actions = hub.quickActions ?? [];

  const handleCreate = async () => {
    if (!formData.label.trim()) return;
    setSaving(true);
    try {
      await teamService.createQuickAction(hub.uuid, {
        label: formData.label,
        description: formData.description || undefined,
        icon: formData.icon,
        color: formData.color,
        gradient: formData.gradient,
        actionType: formData.actionType,
        agentUuid: formData.agentUuid || undefined,
        appUuid: formData.appUuid || undefined,
        flowUuid: formData.flowUuid || undefined,
        targetRoute: formData.targetRoute || undefined,
        hintText: formData.hintText || undefined,
        autoSubmit: formData.autoSubmit,
        displayOrder: actions.length,
        isEnabled: formData.isEnabled,
      });
      setCreating(false);
      setFormData(emptyQuickAction);
      onRefresh();
    } catch (e) {
      console.error("Failed to create quick action:", e);
      showToast("Failed to create quick action", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingUuid || !formData.label.trim()) return;
    setSaving(true);
    try {
      await teamService.updateQuickAction(hub.uuid, editingUuid, {
        label: formData.label,
        description: formData.description || undefined,
        icon: formData.icon,
        color: formData.color,
        gradient: formData.gradient,
        actionType: formData.actionType,
        agentUuid: formData.agentUuid || undefined,
        appUuid: formData.appUuid || undefined,
        flowUuid: formData.flowUuid || undefined,
        targetRoute: formData.targetRoute || undefined,
        hintText: formData.hintText,
        autoSubmit: formData.autoSubmit,
        isEnabled: formData.isEnabled,
      });
      setEditingUuid(null);
      setFormData(emptyQuickAction);
      onRefresh();
    } catch (e) {
      console.error("Failed to update quick action:", e);
      showToast("Failed to update quick action", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (uuid: string) => {
    const confirmed = await confirm({
      title: "Delete Quick Action",
      message: "Are you sure you want to delete this quick action?",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeletingUuid(uuid);
    try {
      await teamService.deleteQuickAction(hub.uuid, uuid);
      onRefresh();
    } catch (e) {
      console.error("Failed to delete quick action:", e);
      showToast("Failed to delete quick action", "error");
    } finally {
      setDeletingUuid(null);
    }
  };

  const handleToggle = async (action: TeamHubQuickActionSummary) => {
    try {
      await teamService.updateQuickAction(hub.uuid, action.uuid, {
        isEnabled: !action.isEnabled,
      });
      onRefresh();
    } catch (e) {
      console.error("Failed to toggle quick action:", e);
      showToast("Failed to toggle quick action", "error");
    }
  };

  const startEdit = (action: TeamHubQuickActionSummary) => {
    setCreating(false);
    setEditingUuid(action.uuid);
    setFormData({
      label: action.label,
      description: action.description || "",
      icon: action.icon,
      color: action.color,
      gradient: action.gradient,
      actionType: action.actionType,
      agentUuid: action.agentUuid || "",
      appUuid: action.appUuid || "",
      flowUuid: action.flowUuid || "",
      targetRoute: action.targetRoute || "",
      hintText: action.hintText || "",
      autoSubmit: action.autoSubmit ?? false,
      isEnabled: action.isEnabled,
    });
  };

  const handleReorder = async (uuid: string, direction: "up" | "down") => {
    if (reordering) return;
    const idx = actions.findIndex((a) => a.uuid === uuid);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= actions.length) return;
    setReordering(true);
    try {
      await Promise.all([
        teamService.updateQuickAction(hub.uuid, actions[idx].uuid, {
          displayOrder: actions[swapIdx].displayOrder,
        }),
        teamService.updateQuickAction(hub.uuid, actions[swapIdx].uuid, {
          displayOrder: actions[idx].displayOrder,
        }),
      ]);
      onRefresh();
    } catch (e) {
      console.error("Failed to reorder:", e);
      showToast("Failed to reorder", "error");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-interactive-hover transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-linear-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
            <Zap className="h-5 w-5 text-blue-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-content-primary">Quick Actions</h3>
            <p className="text-xs text-content-tertiary">
              {actions.length} {actions.length === 1 ? "action" : "actions"} configured
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-content-quaternary" />
        ) : (
          <ChevronDown className="h-4 w-4 text-content-quaternary" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border-primary">
          {/* Existing items */}
          {actions.length > 0 && (
            <div className="divide-y divide-border-primary">
              {actions.map((action, idx) => {
                const Icon = resolveIcon(action.icon);
                const isEditing = editingUuid === action.uuid;

                if (isEditing) {
                  return (
                    <div key={action.uuid} className="p-5 bg-surface-secondary/30">
                      <QuickActionForm
                        data={formData}
                        onChange={setFormData}
                        agents={agents}
                        apps={apps}
                        flows={flows}
                      />
                      <div className="flex items-center gap-2 mt-4">
                        <button
                          onClick={handleUpdate}
                          disabled={saving || !formData.label.trim()}
                          className="px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-1.5"
                        >
                          <Save className="h-3.5 w-3.5" />
                          {saving ? "Saving..." : "Save Changes"}
                        </button>
                        <button
                          onClick={() => {
                            setEditingUuid(null);
                            setFormData(emptyQuickAction);
                          }}
                          className="px-4 py-2 bg-surface-secondary text-content-secondary rounded-lg text-sm font-medium hover:bg-interactive-active transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={action.uuid}
                    className={`flex items-center gap-4 px-5 py-3.5 group ${action.isEnabled ? "" : "opacity-50"}`}
                  >
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleReorder(action.uuid, "up")}
                        disabled={idx === 0}
                        className="p-0.5 text-content-quaternary hover:text-content-secondary disabled:opacity-20 transition-colors"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleReorder(action.uuid, "down")}
                        disabled={idx === actions.length - 1}
                        className="p-0.5 text-content-quaternary hover:text-content-secondary disabled:opacity-20 transition-colors"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>
                    <div
                      className={`h-9 w-9 rounded-lg bg-linear-to-br ${action.gradient} flex items-center justify-center shrink-0`}
                    >
                      <Icon className={`h-4 w-4 ${action.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-content-primary truncate">{action.label}</p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-surface-secondary text-content-quaternary uppercase">
                          {action.actionType}
                        </span>
                      </div>
                      {action.description && (
                        <p className="text-xs text-content-tertiary truncate mt-0.5">{action.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggle(action)}
                        title={action.isEnabled ? "Disable" : "Enable"}
                        className="p-1.5 rounded-md hover:bg-interactive-hover transition-colors"
                      >
                        {action.isEnabled ? (
                          <ToggleRight className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-content-quaternary" />
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(action)}
                        className="p-1.5 rounded-md hover:bg-interactive-hover transition-colors text-content-tertiary hover:text-content-primary"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(action.uuid)}
                        disabled={deletingUuid === action.uuid}
                        className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-content-tertiary hover:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Create form */}
          {creating ? (
            <div className="p-5 border-t border-border-primary bg-surface-secondary/20">
              <QuickActionForm data={formData} onChange={setFormData} agents={agents} apps={apps} flows={flows} />
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={handleCreate}
                  disabled={saving || !formData.label.trim()}
                  className="px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {saving ? "Creating..." : "Create Action"}
                </button>
                <button
                  onClick={() => {
                    setCreating(false);
                    setFormData(emptyQuickAction);
                  }}
                  className="px-4 py-2 bg-surface-secondary text-content-secondary rounded-lg text-sm font-medium hover:bg-interactive-active transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 border-t border-border-primary">
              <button
                onClick={() => {
                  setEditingUuid(null);
                  setCreating(true);
                  setFormData(emptyQuickAction);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Add Quick Action
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
